// Updated authService.js with LinkedIn authentication support
import axios from 'axios';
import { getApiUrl, getCorsSettings, getBackendBaseUrl } from '../utils/apiConfig';
import { cleanupLocalStorageAfterAuth } from '../utils/localStorageCleanup';
import { setSentryUser, clearSentryUser } from '../utils/sentry';
import {
  HEADER_KEYS,
  STORAGE_KEYS,
  getStoredOAuthSubUserId,
  clearStoredOAuthSubUserId
} from '../constants/identity';
import { getAcquisitionData } from '../utils/acquisitionTracking';

// API URL for authentication endpoints
const API_URL = getApiUrl('/auth');

// OAuth Client IDs
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID;

/**
 * Helper function to make API requests with secure token management
 * @param {string} endpoint - The API endpoint (e.g., '/google', '/apple')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - The API response
 */
const fetchFromAPI = async (endpoint = '', options = {}) => {
  const timeoutMs = options.timeout || 30000; // Increased timeout
  const corsSettings = getCorsSettings();

  try {
    // Include signup access code if stored (for dev environment gating)
    const signupCodeHeaders = {};
    const signupCode = sessionStorage.getItem('signup_access_code');
    if (signupCode) {
      signupCodeHeaders['x-signup-code'] = signupCode;
    }

    const requestConfig = {
      url: `${API_URL}${endpoint}`,
      timeout: timeoutMs,
      headers: {
        'Content-Type': 'application/json',
        ...signupCodeHeaders,
        ...(options.headers || {})
      },
      withCredentials: true, // CRITICAL: Enable sending/receiving cookies
      ...corsSettings,
      ...options
    };

    // Add access token from localStorage to Authorization header if available
    const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (accessToken && !requestConfig.headers.Authorization) {
      requestConfig.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Debug logging for admin login requests
    if (endpoint === '/admin-login-as') {
      console.log('Making admin login request:', {
        url: requestConfig.url,
        method: requestConfig.method,
        headers: requestConfig.headers,
        data: requestConfig.data
      });
    }

    const response = await axios(requestConfig);
    return response;
  } catch (error) {
    console.error('API request failed:', error);

    // More detailed error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });

      // Handle 401 Unauthorized - dispatch auth:logout event
      // This triggers the AuthContext listener to redirect to login
      if (error.response.status === 401) {
        const errorCode = error.response.data?.code;
        console.log('🔒 401 Unauthorized - dispatching auth:logout event', { errorCode });
        window.dispatchEvent(new CustomEvent('auth:logout', {
          detail: errorCode === 'TOKEN_REVOKED' ? 'Token revoked' : 'Session expired'
        }));
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
    }

    throw error;
  }
};

/**
 * Initialize Google OAuth
 * Uses Google's OAuth 2.0 endpoint for authentication
 * @param {string} orderNumber - Optional order number to associate with the auth flow
 * @returns {string} - The Google OAuth URL to redirect to
 */
export const initiateGoogleAuth = (orderNumber = null, stateStr = null, options = {}) => {
  // Make sure this exactly matches what's in Google Cloud Console
  const REDIRECT_URI = encodeURIComponent(`${window.location.origin}/auth/google/callback`);
  const SCOPE = encodeURIComponent('profile email');

  // Parse state if it was passed as a string, or use default values
  let stateData = {
    returnUrl: '/dashboard'
  };

  try {
    if (stateStr) {
      // If state was passed as a string, parse it
      stateData = JSON.parse(stateStr);
    }
  } catch (e) {
    console.error('Error parsing state string:', e);
  }

  // Add orderNumber to state if provided
  if (orderNumber) {
    stateData.orderNumber = orderNumber;
  }

  // Stringify and encode the state
  const state = encodeURIComponent(JSON.stringify(stateData));

  // Allow overriding prompt (default: consent, use select_account to show account picker)
  const prompt = options.prompt || 'consent';

  console.log(`Initiating Google Auth with redirect URI: ${REDIRECT_URI}`);
  console.log('State data:', stateData);
  console.log('Prompt:', prompt);

  return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=${SCOPE}&access_type=offline&prompt=${prompt}&state=${state}`;
};

/**
 * Initialize LinkedIn OAuth
 * Uses LinkedIn's OAuth 2.0 endpoint for authentication
 * @param {string} orderNumber - Optional order number to associate with the auth flow
 * @returns {string} - The LinkedIn OAuth URL to redirect to
 */
export const initiateLinkedInAuth = (orderNumber = null) => {
  // Make sure this exactly matches what's in LinkedIn Developer Console
  const REDIRECT_URI = encodeURIComponent(`${window.location.origin}/auth/linkedin/callback`);
  const SCOPE = encodeURIComponent('r_emailaddress r_liteprofile');
  
  // Adding state parameter to track the order and return URL
  const state = encodeURIComponent(JSON.stringify({
    returnUrl: '/dashboard',
    orderNumber: orderNumber
  }));
  
  console.log(`Initiating LinkedIn Auth with redirect URI: ${window.location.origin}/auth/linkedin/callback`);
  
  return `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&state=${state}&scope=${SCOPE}`;
};

/**
 * Handle Google OAuth callback
 * @param {string} code - Authorization code from Google
 * @param {string} state - State parameter from Google (contains orderNumber if provided)
 * @returns {Promise<Object>} - User data after authentication
 */
export const handleGoogleCallback = async (code, state) => {
  try {
    console.log('Handling Google callback with code', code.substring(0, 10) + '...');
    
    // Parse the state parameter if it exists
    let orderDetails = {};
    if (state) {
      try {
        orderDetails = JSON.parse(decodeURIComponent(state));
        console.log('Parsed state:', orderDetails);
      } catch (e) {
        console.error('Could not parse state parameter:', e);
      }
    }

    // Prepare the request data - include state so backend can extract returnUrl
    const requestData = { code };

    // Include the state parameter so backend can parse returnUrl
    if (state) {
      requestData.state = state;
    }

    // Only include orderNumber if it exists and is a string
    if (orderDetails.orderNumber) {
      requestData.orderNumber = orderDetails.orderNumber;
    }

    // Include acquisition tracking data (UTM, referrer, affiliate codes)
    const acquisitionData = getAcquisitionData();
    if (acquisitionData) {
      requestData.acquisition = acquisitionData;
    }

    console.log('Sending request data (including state for returnUrl):', requestData);

    const userIdForSubs = getStoredOAuthSubUserId();
    const subscriptionHeaders = userIdForSubs
      ? {
        [HEADER_KEYS.USER_ID]: userIdForSubs,
        [HEADER_KEYS.LEGACY_SUBS_USER_ID]: userIdForSubs
      }
      : {};
    
    const response = await fetchFromAPI('/google/callback', {
      method: 'POST',
      data: requestData,
      headers: subscriptionHeaders
    });
    
    console.log('Google callback successful', response.data);

    // Store the auth token in localStorage for future API calls
    if (response.data.token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      clearStoredOAuthSubUserId();
      console.log('Auth token stored in localStorage');

      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true,
        additionalCleanup: [
          STORAGE_KEYS.OAUTH_SUBS_USER_ID,
          STORAGE_KEYS.LEGACY_OAUTH_SUBS_USER_ID
        ]
      });
    }

    return response.data;
  } catch (error) {
    console.error('Error during Google authentication:', error);
    throw error;
  }
};

/**
 * Handle LinkedIn OAuth callback
 * @param {string} code - Authorization code from LinkedIn
 * @param {string} state - State parameter from LinkedIn (contains orderNumber if provided)
 * @returns {Promise<Object>} - User data after authentication
 */
export const handleLinkedInCallback = async (code, state) => {
  try {
    console.log('Handling LinkedIn callback with code', code.substring(0, 10) + '...');
    
    // Parse the state parameter if it exists
    let orderDetails = {};
    if (state) {
      try {
        orderDetails = JSON.parse(decodeURIComponent(state));
        console.log('Parsed state:', orderDetails);
      } catch (e) {
        console.warn('Could not parse state parameter:', e);
      }
    }

    const response = await fetchFromAPI('/linkedin/callback', {
      method: 'POST',
      data: {
        code,
        orderNumber: orderDetails.orderNumber,
        acquisition: getAcquisitionData()
      }
    });
    
    console.log('LinkedIn callback successful', response.data);
    
    // Store the auth token in localStorage for future API calls
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
      console.log('Auth token stored in localStorage');
      
      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('Error during LinkedIn authentication:', error);
    throw error;
  }
};

/**
 * Initialize Facebook OAuth
 * @param {string} orderNumber - Optional order number to associate with the auth flow
 * @param {string} stateStr - Optional state string for maintaining state
 * @returns {string} - The Facebook OAuth URL to redirect to
 */
export const initiateFacebookAuth = (orderNumber = null, stateStr = null) => {
  const FB_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;
  const REDIRECT_URI = encodeURIComponent(`${window.location.origin}/auth/facebook/callback`);
  const SCOPE = encodeURIComponent('email,public_profile');

  // Parse state if it was passed as a string, or use default values
  let stateData = {
    returnUrl: '/dashboard'
  };

  try {
    if (stateStr) {
      stateData = JSON.parse(stateStr);
    }
  } catch (e) {
    console.error('Error parsing state string:', e);
  }

  // Add orderNumber to state if provided
  if (orderNumber) {
    stateData.orderNumber = orderNumber;
  }

  const state = encodeURIComponent(JSON.stringify(stateData));

  console.log(`Initiating Facebook Auth with redirect URI: ${decodeURIComponent(REDIRECT_URI)}`);
  console.log('State data:', stateData);

  return `https://www.facebook.com/v25.0/dialog/oauth?client_id=${FB_APP_ID}&redirect_uri=${REDIRECT_URI}&scope=${SCOPE}&state=${state}`;
};

/**
 * Handle Facebook OAuth callback
 * @param {string} code - Authorization code from Facebook
 * @param {string} state - State parameter from Facebook (contains orderNumber if provided)
 * @returns {Promise<Object>} - User data after authentication
 */
export const handleFacebookCallback = async (code, state) => {
  try {
    console.log('Handling Facebook callback with code', code.substring(0, 10) + '...');

    // Parse the state parameter if it exists
    let orderDetails = {};
    if (state) {
      try {
        orderDetails = JSON.parse(decodeURIComponent(state));
        console.log('Parsed state:', orderDetails);
      } catch (e) {
        console.error('Could not parse state parameter:', e);
      }
    }

    // Prepare the request data
    const requestData = { code };

    // Include the state parameter so backend can parse returnUrl
    if (state) {
      requestData.state = state;
    }

    if (orderDetails.orderNumber) {
      requestData.orderNumber = orderDetails.orderNumber;
    }

    // Pass the redirect URI so backend can use it for token exchange
    requestData.redirectUri = `${window.location.origin}/auth/facebook/callback`;

    // Include acquisition tracking data
    const acquisitionData = getAcquisitionData();
    if (acquisitionData) {
      requestData.acquisition = acquisitionData;
    }

    const userIdForSubs = getStoredOAuthSubUserId();
    const subscriptionHeaders = userIdForSubs
      ? {
        [HEADER_KEYS.USER_ID]: userIdForSubs,
        [HEADER_KEYS.LEGACY_SUBS_USER_ID]: userIdForSubs
      }
      : {};

    const response = await fetchFromAPI('/facebook/callback', {
      method: 'POST',
      data: requestData,
      headers: subscriptionHeaders
    });

    console.log('Facebook callback successful', response.data);

    // Store the auth token in localStorage for future API calls
    if (response.data.token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      clearStoredOAuthSubUserId();
      console.log('Auth token stored in localStorage');

      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true,
        additionalCleanup: [
          STORAGE_KEYS.OAUTH_SUBS_USER_ID,
          STORAGE_KEYS.LEGACY_OAUTH_SUBS_USER_ID
        ]
      });
    }

    return response.data;
  } catch (error) {
    console.error('Error during Facebook authentication:', error);
    throw error;
  }
};

/**
 * Initialize Apple Sign In
 * @param {string} orderNumber - Optional order number to associate with the auth flow
 * @param {string} stateStr - Optional state string for maintaining state
 * @returns {string} - The Apple OAuth URL to redirect to
 */
export const initiateAppleAuth = (orderNumber = null, stateStr = null) => {
  const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID;

  // Apple form_post must go directly to the backend API endpoint
  // Backend will handle the form_post and redirect back to frontend with token
  // Use getBackendBaseUrl() which returns the base URL WITHOUT /api suffix
  const BACKEND_BASE_URL = getBackendBaseUrl();
  const REDIRECT_URI = encodeURIComponent(`${BACKEND_BASE_URL}/api/auth/apple/callback`);
  const SCOPE = encodeURIComponent('name email');

  // Parse state if it was passed as a string
  // Don't set a default returnUrl - let the backend decide based on subscription status
  let stateData = {};

  try {
    if (stateStr) {
      // If state was passed as a string, parse it
      stateData = JSON.parse(stateStr);
    }
  } catch (e) {
    console.error('Error parsing state string:', e);
  }

  // Add orderNumber to state if provided
  if (orderNumber) {
    stateData.orderNumber = orderNumber;
  }

  // Stringify and encode the state
  const state = encodeURIComponent(JSON.stringify(stateData));

  console.log(`Initiating Apple Auth with backend base: ${BACKEND_BASE_URL}`);
  console.log(`Redirect URI: ${decodeURIComponent(REDIRECT_URI)}`);
  console.log('State data:', stateData);

  // Apple requires form_post when requesting name or email scope
  // Backend will receive the form_post, process auth, and redirect to frontend with token cookie
  return `https://appleid.apple.com/auth/authorize?client_id=${APPLE_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=${SCOPE}&response_mode=form_post&state=${state}`;
};

/**
 * Handle Apple Sign In callback
 * @param {string} code - Authorization code from Apple
 * @param {string} state - State parameter from Apple (contains orderNumber if provided)
 * @param {Object} user - User data from Apple (only sent on first login)
 * @returns {Promise<Object>} - User data after authentication
 */
export const handleAppleCallback = async (code, state, user = null) => {
  try {
    console.log('Handling Apple callback with code', code.substring(0, 10) + '...');

    // Parse the state parameter if it exists
    let orderDetails = {};
    if (state) {
      try {
        orderDetails = JSON.parse(decodeURIComponent(state));
        console.log('Parsed state:', orderDetails);
      } catch (e) {
        console.error('Could not parse state parameter:', e);
      }
    }

    // Prepare the request data
    const requestData = { code };

    // Include orderNumber if it exists
    if (orderDetails.orderNumber) {
      requestData.orderNumber = orderDetails.orderNumber;
    }

    // Include user data if provided (Apple only sends this on first login)
    if (user) {
      requestData.user = user;
    }

    // Include acquisition tracking data (UTM, referrer, affiliate codes)
    const acquisitionData = getAcquisitionData();
    if (acquisitionData) {
      requestData.acquisition = acquisitionData;
    }

    console.log('Sending request data:', requestData);

    const userIdForSubs = getStoredOAuthSubUserId();
    const subscriptionHeaders = userIdForSubs
      ? {
        [HEADER_KEYS.USER_ID]: userIdForSubs,
        [HEADER_KEYS.LEGACY_SUBS_USER_ID]: userIdForSubs
      }
      : {};

    const response = await fetchFromAPI('/apple/callback', {
      method: 'POST',
      data: requestData,
      headers: subscriptionHeaders
    });

    console.log('Apple callback successful', response.data);

    // Store the auth token in localStorage for future API calls
    if (response.data.token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      clearStoredOAuthSubUserId();
      console.log('Auth token stored in localStorage');

      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true,
        additionalCleanup: [
          STORAGE_KEYS.OAUTH_SUBS_USER_ID,
          STORAGE_KEYS.LEGACY_OAUTH_SUBS_USER_ID
        ]
      });
    }

    return response.data;
  } catch (error) {
    console.error('Error during Apple authentication:', error);
    throw error;
  }
};

/**
 * Refresh access token using httpOnly refresh token cookie
 * @returns {Promise<Object>} - New access token data or null if refresh failed
 */
export const refreshAccessToken = async () => {
  try {
    console.log('🔄 Attempting to refresh access token...');

    const response = await axios.post(
      `${API_URL}/refresh`,
      {},
      {
        withCredentials: true, // Send httpOnly cookie
        timeout: 10000,
      }
    );

    if (response.data && response.data.accessToken) {
      // Store the new access token in localStorage
      localStorage.setItem('auth_token', response.data.accessToken);
      console.log('✅ Access token refreshed successfully');

      return {
        accessToken: response.data.accessToken,
        user: response.data.user,
        expiresIn: response.data.expiresIn || 900,
      };
    }

    console.log('❌ No access token in refresh response');
    return null;
  } catch (error) {
    // Check if it's an auth error (expired/invalid refresh token)
    if (error.response?.status === 401) {
      console.log('🔒 Refresh token expired or invalid, user needs to login');
      // Clear any stale tokens
      localStorage.removeItem('auth_token');
    } else {
      console.error('❌ Token refresh failed:', error.message);
    }
    return null;
  }
};

/**
 * Check if user is authenticated
 * Tries to refresh token if localStorage is empty but refresh cookie might exist
 * @returns {Promise<boolean>} - Whether the user is authenticated
 */
export const isAuthenticated = async () => {
  try {
    // Check if we have a token in localStorage
    const token = localStorage.getItem('auth_token');
    if (token) {
      console.log('✅ Auth token found in localStorage');
      return true;
    }

    // No token in localStorage - try to refresh using httpOnly cookie
    console.log('🔄 No localStorage token, attempting silent refresh...');
    const refreshResult = await refreshAccessToken();

    if (refreshResult && refreshResult.accessToken) {
      console.log('✅ Silent refresh successful, user is authenticated');
      return true;
    }

    console.log('❌ No valid session found');
    return false;
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};

// In-memory cache for getCurrentUser to avoid redundant /auth/me calls.
// Multiple components (AuthContext, MainLayout, TestClockTimeIndicator, verifyToken)
// all call getCurrentUser independently, resulting in 10-14 calls per session.
let _currentUserCache = { data: null, token: null, expiresAt: 0 };
const CURRENT_USER_CACHE_TTL = 5000; // 5 seconds

/**
 * Get current user profile (cached for 5s to deduplicate concurrent calls)
 * @param {Object} options - { skipCache: true } to force fresh fetch
 * @returns {Promise<Object>} - User profile data
 */
export const getCurrentUser = async (options = {}) => {
  try {
    // Check if we have a token
    const token = localStorage.getItem('auth_token');

    if (!token) {
      _currentUserCache = { data: null, token: null, expiresAt: 0 };
      throw new Error('No valid token available');
    }

    // Return cached data if valid (same token, not expired, not forced)
    if (
      !options.skipCache &&
      _currentUserCache.data &&
      _currentUserCache.token === token &&
      Date.now() < _currentUserCache.expiresAt
    ) {
      return _currentUserCache.data;
    }

    const response = await fetchFromAPI('/me');

    // Set Sentry user context when we successfully fetch user data
    if (response.data) {
      setSentryUser({
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        plan_type: response.data.plan_type,
        role: response.data.role
      });

      // Cache the result
      _currentUserCache = {
        data: response.data,
        token,
        expiresAt: Date.now() + CURRENT_USER_CACHE_TTL,
      };
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

/** Invalidate the getCurrentUser cache (call on login, logout, balance changes) */
export const invalidateCurrentUserCache = () => {
  _currentUserCache = { data: null, token: null, expiresAt: 0 };
};

/**
 * Sign out the user
 * @returns {Promise<Object>} - Response data
 */
export const signOut = async () => {
  try {
    console.log('🚪 Signing out...');

    const response = await fetchFromAPI('/signout', {
      method: 'POST'
    });

    // Clear localStorage token
    localStorage.removeItem('auth_token');
    console.log('✅ Auth token cleared from localStorage');

    // Clear Sentry user context
    clearSentryUser();

    // Clear the user recognition cookie
    const { cookieManager } = await import('../utils/cookieManager');
    cookieManager.clearUserUuid();
    console.log('✅ User recognition cookie cleared');

    return response.data;
  } catch (error) {
    console.error('❌ Error signing out:', error);

    // Clear token anyway on error
    localStorage.removeItem('auth_token');

    // Clear Sentry user context
    clearSentryUser();

    try {
      const { cookieManager } = await import('../utils/cookieManager');
      cookieManager.clearUserUuid();
    } catch (cookieError) {
      console.error('Failed to clear user recognition cookie:', cookieError);
    }

    throw error;
  }
};

/**
 * Link order with a user account
 * @param {string} orderNumber - The order number to link
 * @returns {Promise<Object>} - Response data
 */
export const linkOrderWithUser = async (orderNumber) => {
  try {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetchFromAPI('/link-order', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      data: { orderNumber }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error linking order with user:', error);
    throw error;
  }
};

/**
 * Development/Debug function for direct login without OAuth
 * Only available in development environments
 */
export const debugLogin = async (email, name = 'Test User', role = 'customer') => {
  try {
    const response = await fetchFromAPI('/debug-login', {
      method: 'POST',
      data: { email, name, role }
    });
    
    // Store the auth token in localStorage
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
      
      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('Error during debug login:', error);
    throw error;
  }
};

/**
 * Login with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} - User data after authentication
 */
export const loginWithEmail = async (email, password) => {
  try {
    console.log('🔐 Logging in with email:', email);

    const response = await fetchFromAPI('/login', {
      method: 'POST',
      data: { email, password }
    });

    // Store token in localStorage
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
      console.log('✅ Token stored in localStorage');

      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true
      });
    }

    console.log('✅ Login successful');
    return response.data;
  } catch (error) {
    console.error('❌ Login failed:', error);
    throw error;
  }
};

/**
 * Register a new user with email and password.
 * Does NOT log the user in — the backend stashes a pending registration and
 * emails a verification link. The account is created only after the link is
 * clicked (see verifyEmail).
 * @param {string} name - User's display name
 * @param {string} email - User's email address
 * @param {string} password - User's password (min 8 chars)
 * @returns {Promise<Object>} - { success, message } ("check your email")
 */
export const signupWithEmail = async (name, email, password) => {
  try {
    console.log('📝 Signing up with email:', email);

    const response = await fetchFromAPI('/signup', {
      method: 'POST',
      data: { name, email, password }
    });

    console.log('✅ Signup request accepted (verification email sent)');
    return response.data;
  } catch (error) {
    console.error('❌ Signup failed:', error);
    throw error;
  }
};

/**
 * Verify an email signup via the token from the verification link.
 * On success the backend creates/activates the account and logs the user in.
 * @param {string} token - Verification token from the email link
 * @returns {Promise<Object>} - User data + token after verification
 */
export const verifyEmail = async (token) => {
  try {
    console.log('📧 Verifying email signup');

    const response = await fetchFromAPI('/verify-email', {
      method: 'POST',
      data: { token }
    });

    // Store token in localStorage (backend logs the user in on verification)
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
      console.log('✅ Token stored in localStorage');

      // Clean up outdated localStorage data after successful authentication
      cleanupLocalStorageAfterAuth({
        preservePreferences: true
      });
    }

    console.log('✅ Email verification successful');
    return response.data;
  } catch (error) {
    console.error('❌ Email verification failed:', error);
    throw error;
  }
};

/**
 * Request a password reset email. Always resolves the same way regardless of
 * whether the account exists (the backend prevents email enumeration).
 * Also used by OAuth-only users to set a password for the first time.
 * @param {string} email - Account email
 * @returns {Promise<Object>} - { success, message }
 */
export const requestPasswordReset = async (email) => {
  try {
    console.log('🔑 Requesting password reset for:', email);

    const response = await fetchFromAPI('/forgot-password', {
      method: 'POST',
      data: { email }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Password reset request failed:', error);
    throw error;
  }
};

/**
 * Set a new password using the token from the reset email link.
 * Does NOT log the user in — the caller should redirect to /login afterwards.
 * @param {string} token - Reset token from the email link
 * @param {string} password - New password (min 8 chars)
 * @returns {Promise<Object>} - { success, message }
 */
export const resetPassword = async (token, password) => {
  try {
    console.log('🔑 Resetting password');

    const response = await fetchFromAPI('/reset-password', {
      method: 'POST',
      data: { token, password }
    });

    console.log('✅ Password reset successful');
    return response.data;
  } catch (error) {
    console.error('❌ Password reset failed:', error);
    throw error;
  }
};

/**
 * Admin login as another user
 * @param {string} targetUserId - Target user's ID to impersonate
 * @returns {Promise<Object>} - User data and admin session info
 */
export const adminLoginAsUser = async (targetUserId) => {
  try {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('No admin token found. Please login as admin first.');
    }
    
    const response = await fetchFromAPI('/admin-login-as', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      data: { targetUserId }
    });
    
    // Store the new auth token
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    
    // Store admin session info for UI purposes
    if (response.data.isAdminSession && response.data.adminUser) {
      localStorage.setItem('admin_session_info', JSON.stringify({
        isAdminSession: true,
        adminUser: response.data.adminUser,
        targetUser: response.data.user
      }));
    }
    
    return response.data;
  } catch (error) {
    console.error('Error during admin login as user:', error);
    throw error;
  }
};

/**
 * Return to admin account from impersonated session
 * @returns {Promise<Object>} - Admin user data
 */
export const returnToAdminAccount = async () => {
  try {
    // Get admin session info
    const adminSessionInfo = localStorage.getItem('admin_session_info');
    if (!adminSessionInfo) {
      throw new Error('No admin session found');
    }
    
    const sessionData = JSON.parse(adminSessionInfo);
    if (!sessionData.isAdminSession || !sessionData.adminUser) {
      throw new Error('Invalid admin session');
    }

    // Call the return to admin API endpoint
    const response = await fetchFromAPI('/return-to-admin', {
      method: 'POST',
      data: { adminUserId: sessionData.adminUser.id }
    });
    
    // Store the new admin token
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    
    // Clear admin session info
    localStorage.removeItem('admin_session_info');
    
    return response.data;
  } catch (error) {
    console.error('Error returning to admin account:', error);
    
    // Fallback to debug login if API fails
    try {
      const adminSessionInfo = localStorage.getItem('admin_session_info');
      if (adminSessionInfo) {
        const sessionData = JSON.parse(adminSessionInfo);
        if (sessionData.adminUser) {
          const response = await debugLogin(sessionData.adminUser.email, sessionData.adminUser.name, 'admin');
          localStorage.removeItem('admin_session_info');
          return response;
        }
      }
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
    }
    
    throw error;
  }
};

/**
 * Check if current session is an admin impersonation session
 * @returns {Object|null} - Admin session info or null
 */
export const getAdminSessionInfo = () => {
  try {
    const adminSessionInfo = localStorage.getItem('admin_session_info');
    if (adminSessionInfo) {
      return JSON.parse(adminSessionInfo);
    }
    return null;
  } catch (error) {
    console.error('Error getting admin session info:', error);
    return null;
  }
};

/**
 * Clear admin session info
 */
export const clearAdminSession = () => {
  localStorage.removeItem('admin_session_info');
};

export default {
  initiateGoogleAuth,
  handleGoogleCallback,
  initiateLinkedInAuth,
  handleLinkedInCallback,
  initiateFacebookAuth,
  handleFacebookCallback,
  initiateAppleAuth,
  handleAppleCallback,
  isAuthenticated,
  signOut,
  getCurrentUser,
  linkOrderWithUser,
  debugLogin,
  loginWithEmail,
  signupWithEmail,
  verifyEmail,
  requestPasswordReset,
  resetPassword,
  adminLoginAsUser,
  returnToAdminAccount,
  getAdminSessionInfo,
  clearAdminSession,
  refreshAccessToken,
  invalidateCurrentUserCache
};
